import { AlertBanner } from '@containers/Layout/AlertBanner';
import { AppBar } from '@containers/Layout/AppBar';
import { AppMenu } from '@containers/Layout/AppBar/AppMenu';
import { Avatar } from '@containers/Layout/AppBar/Avatar';
import { Notifications } from '@containers/Layout/AppBar/Notifications';
import { Breadcrumb } from '@containers/Layout/Breadcrumb';
import { Disclaimer } from '@containers/Layout/Disclaimer';
import {
  ContentContainer,
  LayoutContent,
} from '@containers/Layout/LayoutContext';
import { NavMenu } from '@containers/Layout/NavMenu';
import {
  IntegrationProviders,
  IntegrationStates,
  IntegrationTypes,
  NavigationStyles,
  PageConfigurationTypes,
} from '@generated/graphql';
import type { Integration } from '@interfaces/integrations';
import ErrorBoundary from '@ovComponents/3-pattern/errorBoundary/errorBoundary';
import Downtime from '@ovComponents/4-module/downtime/downtime';
import {
  getZendeskKeys,
  ZendeskWidget,
} from '@ovComponents/4-module/widgets/chatAgent/zendesk';
import { UserContext } from '@providers/userContextProvider';
import { ACTIVE_ORGANIZATION_QUERY_PARAM } from '@utils/activeOrganization';
import { entityName } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import {
  APP_BAR_HEIGHT,
  BootSpinner,
  Box,
  navCollapsedWidth,
  navExpandedWidth,
  navTransitionDuration,
  navTransitionEasing,
  Stack,
  useTheme,
  useToast,
} from 'hephaestus/web';
import { isUndefined } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { usePermission } from '../../hooks/usePermission';

const pageTypeMap: Record<string, PageConfigurationTypes> = {
  clients: PageConfigurationTypes.Individual,
  nonIndividualClients: PageConfigurationTypes.NonIndividual,
  households: PageConfigurationTypes.Household,
  household: PageConfigurationTypes.Household,
};

const pageTypes = [
  PageConfigurationTypes.Individual,
  PageConfigurationTypes.NonIndividual,
  PageConfigurationTypes.Household,
];

const ALERT_BANNER_IGNORE_PATHS: readonly string[] = [
  'settings',
  'reconciliation',
  'orgSettings',
];

// read query params once per path (memoized) to avoid drift across effects
const useQueryParams = () =>
  useMemo(() => new URLSearchParams(window.location.search), []);

export const Layout = () => {
  const navigate = useNavigate();
  const { enqueueToast } = useToast();
  const {
    userContext,
    displayMenuBar,
    closed,
    integrations,
    // ^^ is needed to conditionally load 3rd party integration scripts
    // e.g for Zendesk and Fraud Detection (Simility)
    applicableDowntimeSchedule,
    organizationLoading,
    organizationFetchFailed,
    featureFlagsResolved,
  } = useContext(UserContext);

  const canReadNotification = usePermission('read:notification');
  const alertBannersEnabled = useFeatureFlag(FlagsmithFlags.alert_banners);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const theme = useTheme();

  // This it to make pages scroll to top after every navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // RouterLink appends the active org id so it survives opening a link in a new
  // tab. For same-tab navigations the param is redundant (session already
  // holds it), so strip it here to keep the address bar clean.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has(ACTIVE_ORGANIZATION_QUERY_PARAM)) return;

    params.delete(ACTIVE_ORGANIZATION_QUERY_PARAM);
    const search = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location, navigate]);

  // read query once to prevent subtle differences
  const query = useQueryParams();

  // This it to redirect to root path on downtime
  useEffect(() => {
    if (applicableDowntimeSchedule) navigate('/');
  }, [navigate, applicableDowntimeSchedule]);

  const splitPath = pathname.split('/').filter(Boolean);
  const pageTypeFromPath = pageTypeMap[splitPath[splitPath.length - 2]];
  const hasNotifications = canReadNotification;
  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.Simple;
  const navWidth = closed ? navCollapsedWidth : navExpandedWidth;

  // conditionally load the Simility fraud detection scripts
  useEffect(() => {
    const conditionallyInjectSimilityScripts = (
      orgIntegrations: Integration[],
    ) => {
      const config = orgIntegrations.find(
        ({ type, provider, state }) =>
          type === IntegrationTypes.FraudDetection &&
          provider === IntegrationProviders.Simility &&
          state === IntegrationStates.Active,
      )?.configuration;

      if (!config?.pageKeys || !config.fnclsValue || !config.scriptInjectURL)
        return;

      const CMID = window.sessionStorage.getItem('CMID');
      if (!CMID) {
        console.warn(
          'Missing CMID. Will abort Simility fraud management initialization for ORG client!',
        );
        return;
      }

      const { scriptInjectURL, fnclsValue, pageKeys = '' } = config;
      const pageKeysList = pageKeys.split(/\s*,\s*/);

      const page = pageKeysList.find((entry: string) => {
        const match = pathname.match(/\/+([a-zA-Z]+)\/?.*/);
        return match?.at(1) === entry;
      });

      if (!page) return;

      // add/update script data values for this path
      let dataIslandScript = document.querySelector('script[fncls]');
      if (!dataIslandScript) {
        dataIslandScript = document.createElement('script');
        dataIslandScript.setAttribute('type', 'application/json');
        dataIslandScript.setAttribute('fncls', fnclsValue);
        try {
          document.body.appendChild(dataIslandScript);
        } catch (err) {
          console.error('Error injecting fraud detection data script:', err);
        }
      }

      dataIslandScript.textContent = JSON.stringify({
        f: CMID,
        s: page.toUpperCase(),
      });

      // add Simility script
      let fraudnetInitScript = document.querySelector('#fraudnet-init');
      if (!fraudnetInitScript) {
        fraudnetInitScript = document.createElement('script');
        fraudnetInitScript.setAttribute('id', 'fraudnet-init');
        fraudnetInitScript.setAttribute('src', scriptInjectURL);
        try {
          document.body.appendChild(fraudnetInitScript);
        } catch (err) {
          console.error('Error injecting fraud detection init script:', err);
        }
      }
    };

    if (userContext) {
      conditionallyInjectSimilityScripts(integrations);
    }
  }, [pathname, userContext, integrations]);

  // handle `custodianmeta` redirects into OV — currently only the credentials
  // manager success toast via traceId handshake. The logout-on-redirect cases
  // (status=LOGOUT, loggedOutMessage=inactive|changeusername) are handled in an
  // earlier layer in Auth0Provider's mount-effect pre-flight. (OVT-5794)
  useEffect(() => {
    const custodianMeta = query.get('custodianmeta') || '';
    if (!custodianMeta) return;

    const traceId = custodianMeta.match(/traceId:([^&]+)/)?.[1];
    if (!traceId) return;

    const storedTraceId = window.localStorage.getItem(
      'custodianCredentialsManagerTraceId',
    );
    if (traceId !== storedTraceId) return;

    window.localStorage.removeItem('custodianCredentialsManagerTraceId');
    // UX: this is the "settings updated" case. Can be updated when other cases arise
    enqueueToast('Settings updated successfully', { variant: 'success' });
  }, []);

  const pageOptions = () => {
    switch (pageTypeFromPath) {
      case PageConfigurationTypes.Individual:
        return userContext?.role?.individualPageConfiguration?.options;
      case PageConfigurationTypes.NonIndividual:
        return userContext?.role?.nonIndividualPageConfiguration?.options;
      case PageConfigurationTypes.Household:
        return userContext?.role?.householdPageConfiguration?.options;
      default:
        return undefined;
    }
  };

  const options = pageOptions();

  return (
    <>
      {displayMenuBar && (
        <AppBar
          toggleMobileDrawer={() => {
            setMobileOpen((prev) => !prev);
          }}
          sx={{
            position: 'fixed',
            // Drawer default zIndex (1200) > AppBar (1100); raise so bar
            // shadow casts onto side nav instead of sitting under it.
            zIndex: (t) => t.zIndex.drawer + 1,
          }}
        />
      )}
      <Stack sx={{ mt: displayMenuBar ? APP_BAR_HEIGHT : 0 }}>
        {organizationFetchFailed ? (
          <ErrorBoundary />
        ) : organizationLoading || !featureFlagsResolved ? (
          <BootSpinner />
        ) : applicableDowntimeSchedule ? (
          <>
            {!displayMenuBar && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: isUnifiedClientExperience ? 'center' : 'start',
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    maxWidth: '1080px',
                    width: '100%',
                    height: '32px',
                    mb: 2,
                    ml: { xs: 0, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'end',
                      alignItems: 'center',
                      marginLeft: 'auto',
                    }}
                  >
                    <AppMenu showOnlyLogout>
                      <Avatar />
                    </AppMenu>
                  </Box>
                </Box>
              </Box>
            )}
            <Downtime downtime={applicableDowntimeSchedule} />
          </>
        ) : (
          <Box
            sx={{
              justifyContent: 'start',
              alignItems: 'center',
              display: 'flex',
              height: '100%',
            }}
          >
            {!isUnifiedClientExperience && (
              <NavMenu
                setMobileOpen={setMobileOpen}
                mobileOpen={mobileOpen}
                displayMenuBar={displayMenuBar}
              />
            )}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                boxSizing: 'border-box',
                transition: `margin-left ${navTransitionDuration}ms ${navTransitionEasing}`,
                ml: {
                  lg: !isUnifiedClientExperience ? `${navWidth}px` : 0,
                },
              }}
            >
              {alertBannersEnabled &&
                !ALERT_BANNER_IGNORE_PATHS.includes(splitPath[0]) && (
                  <AlertBanner />
                )}
              {pageTypes.includes(pageTypeFromPath) &&
                options?.enableContent && <Disclaimer options={options} />}
              {/*  /!* Note: The breadcrumb for "UnifiedClientExperience" when "displayMenuBar" is set to true is displayed on pages such as viewPageVisual *!/*/}
              <LayoutContent>
                <Box
                  sx={{
                    boxSizing: 'border-box',
                    minWidth: 0,
                    px: 2.5,
                    pt: isUnifiedClientExperience && displayMenuBar ? 2.5 : 0,
                    pb: 2.5,
                  }}
                >
                  {!isUnifiedClientExperience && displayMenuBar && (
                    <ContentContainer transition>
                      <Breadcrumb sx={{ py: 2.25 }} />
                    </ContentContainer>
                  )}
                  {!displayMenuBar && (
                    <ContentContainer transition>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 1,
                          mb: 2,
                        }}
                      >
                        <Breadcrumb />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            marginLeft: 'auto',
                            gap: 0.5,
                          }}
                        >
                          {hasNotifications && <Notifications />}
                          <AppMenu>
                            <Avatar />
                          </AppMenu>
                        </Box>
                      </Box>
                    </ContentContainer>
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Outlet />
                  </Box>
                </Box>
              </LayoutContent>
              {/* Temporary Zendesk workaround. Refactor when adding new chat providers. */}
              {userContext?.accessType &&
                !isUndefined(getZendeskKeys(integrations)) && (
                  <ZendeskWidget
                    widgetStyle={{ color: theme.color.primary }}
                    accessType={userContext?.accessType}
                    user={entityName(userContext)}
                    scriptKeys={getZendeskKeys(integrations)}
                  />
                )}
            </Box>
          </Box>
        )}
      </Stack>
    </>
  );
};
